import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import PropTypes from 'prop-types'

import {
  Box,
  Button,
  Fade,
  FormControl,
  InputAdornment,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { connectStoreModalNextClicked, connectStoreModalShown } from '@wix/bi-logger-modalyst/dist/src/v2'
import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'

import { useBiEvents } from 'shared/bi/hooks'
import { LoadingButton, RadioTile, SuccessSpinner } from 'shared/components/atoms'
import { DataModal, StoreIntegrationCheckWarning } from 'shared/components/organisms'
import { connectableIntegrations, SHOPIFY, WIX } from 'shared/constants/integrations'
import { useTranslation } from 'shared/hooks'
import { SadFace } from 'shared/icons'
import { Shopify, WixLogo } from 'shared/icons/external'
import { getFieldErrorMessage, submitForm, useRemoteErrors } from 'shared/utils/forms'
import { extractShopifyStoreId } from 'shared/utils/integrations'

import {
  STEP_START,
  STEP_INPUT_SHOPIFY_STORE_NAME,
  STEP_AUTHORIZATION_CHECK_FAILED,
  STEP_BACK_FROM_PERMISSIONS_GRANT,
  STEP_CONFIRMATION_FAILED,
  STEP_AUTHORIZATION_CREATED,
  STEP_AUTHORIZATION_CHECK_IN_PROGRESS,
} from 'retailer/stores/ConnectStoreStore/constants'

import style from './ConnectStoreModal.module.scss'

const SelectPlatformContent = observer(({ connectStoreStore }) => {
  const {
    integrationType,
    setConnectToPlatform,
    isAuthorizationInProgress,
  } = connectStoreStore
  const handleChange = (ev, value) => setConnectToPlatform(value)
  return (
    <FormControl fullWidth disabled={isAuthorizationInProgress}>
      <RadioGroup
        row className={style.RadioTileGroup}
        required value={integrationType || ''} onChange={handleChange}
      >
        <RadioTile
          value={WIX}
          icon={WixLogo} label="Wix"
          data-testid="connectStoreModal_platformTileWix"
        />
        <RadioTile
          value={SHOPIFY}
          icon={Shopify} label="Shopify"
          data-testid="connectStoreModal_platformTileShopify"
        />
      </RadioGroup>
    </FormControl>
  )
})

SelectPlatformContent.propTypes = {
  connectStoreStore: PropTypes.shape({
    integrationType: PropTypes.oneOf([WIX, SHOPIFY]),
    setConnectToPlatform: PropTypes.func.isRequired,
    isAuthorizationInProgress: PropTypes.bool.isRequired,
  }).isRequired,
}

const InputShopifyNameContent = observer(forwardRef(({ connectStoreStore }, ref) => {
  const {
    submitStoreName,
    setStoreNameFormValid,
    isAuthorizationInProgress,
    storeIntegration,
  } = connectStoreStore
  const { authorizationCheckRemoteErrors } = storeIntegration || {}

  const { t } = useTranslation('myStores')
  const { correlationId } = useBiEvents({ correlateWithLocation: true })

  const { handleSubmit, register, errors, formState, setError, setValue } = useForm({
    defaultValues: { storeName: '' },
    mode: 'onChange',
  })
  const { isValid } = formState
  const formRef = useRef(null)

  // sync react hook form valid state with store
  useEffect(() => setStoreNameFormValid(isValid), [isValid])

  const { nonFieldErrors } = useRemoteErrors(authorizationCheckRemoteErrors, setError)

  const onSubmit = data => submitStoreName(data, { correlationId })

  useImperativeHandle(ref, () => ({
    submit: () => submitForm(formRef)
  }))

  const handleOnPaste = event => {
    try {
      const clipboardData = event.clipboardData || window.clipboardData
      const value = extractShopifyStoreId(clipboardData.getData('Text'))
      event.stopPropagation()
      event.preventDefault()
      setValue('storeName', value, { shouldDirty: true, shouldValidate: true })
    } catch { }
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <TextField
        fullWidth
        disabled={isAuthorizationInProgress}
        variant="outlined"
        className={style.StoreNameField}
        label={t('connectStoreModal.storeUrlField.label')}
        name="storeName"
        inputRef={register({
          required: t('connectStoreModal.storeUrlField.requiredError'),
          pattern: {
            value: /^[a-z0-9-]+$/i,
            message: t('connectStoreModal.storeUrlField.patternError')
          },
        })}
        error={!!errors.storeName}
        helperText={getFieldErrorMessage(errors, 'storeName')}
        inputProps={{ onPaste: handleOnPaste }}
        InputProps={{
          autoFocus: true,
          startAdornment: <InputAdornment position="start">https://</InputAdornment>,
          endAdornment: <InputAdornment position="end">.myshopify.com</InputAdornment>,
        }}
      />

      {nonFieldErrors && (
        <Alert severity="error">
          {nonFieldErrors?.join('')}
        </Alert>
      )}

      <input type="submit" style={{ display: 'none' }} />
    </form>
  )
}))

InputShopifyNameContent.propTypes = {
  connectStoreStore: PropTypes.shape({
    submitStoreName: PropTypes.func.isRequired,
    setStoreNameFormValid: PropTypes.func.isRequired,
    isAuthorizationInProgress: PropTypes.bool.isRequired,
    storeIntegration: PropTypes.shape({
      authorizationCheckRemoteErrors: PropTypes.object,
    }),
  }).isRequired,
}

/**
 * Modal content after the authorization check returns a response that indicates issues
 */
const AuthorizationCheckFailedContent = observer(({ integrationType, checkResults }) => {
  return (
    <div className={style.AuthorizationCheckWarning}>
      <SadFace className={style.WarningIcon} />
      <StoreIntegrationCheckWarning
        integrationType={integrationType}
        results={checkResults}
        TitleProps={{
          variant: 'h6',
          paragraph: true
        }}
      />
    </div>
  )
})

AuthorizationCheckFailedContent.propTypes = {
  integrationType: PropTypes.oneOf(connectableIntegrations).isRequired,
  checkResults: PropTypes.shape({
    canInstallIntegration: PropTypes.bool.isRequired,
    hasCurrentAuthorization: PropTypes.bool.isRequired,
    isAuthorizedOnAnotherBusiness: PropTypes.bool.isRequired,
    hasAuthorizationInRemoval: PropTypes.bool.isRequired,
    installationExistsForUser: PropTypes.string,
  })
}

const AuthorizationInProgressContent = observer(({ success }) => {
  const { t } = useTranslation('myStores')
  return (
    <Box textAlign="center">
      <SuccessSpinner success={success} size={54} mx="auto" my={2} />
      <Fade in={success}>
        <Typography>{t('connectStoreModal.storeConnected.message')}</Typography>
      </Fade>
    </Box>
  )
})

AuthorizationInProgressContent.propTypes = {
  success: PropTypes.bool.isRequired,
}

const ConfirmationFailedContent = observer(({ storeIntegrationStore }) => {
  const { confirmAuthorizationRemoteErrors } = storeIntegrationStore
  const { allErrors } = useRemoteErrors(confirmAuthorizationRemoteErrors)
  return (
    <Alert severity="error">
      {allErrors?.join('')}
    </Alert>
  )
})

ConfirmationFailedContent.propTypes = {
  storeIntegrationStore: PropTypes.shape({
    confirmAuthorizationRemoteErrors: PropTypes.object,
  })
}

const ConnectStoreModal = ({ connectStoreStore, onExited }) => {
  const modalRef = useRef(null)
  const shopifyNameFormRef = useRef(null)
  const { correlationId, logBiEvent } = useBiEvents({ correlateWithLocation: true })

  const { t } = useTranslation('myStores')

  const {
    step,
    businessId,
    isAuthorizationInProgress,
    showPrimaryAction,
    primaryActionDisabled,
    primaryActionLabel,
    showCancelAction,
    cancelActionDisabled,
    confirmIntegrationType,
    storeIntegration,
    checkAuthorizationState,
    integrationType,
    reset,
  } = connectStoreStore

  const biParams = { correlationId, targetStoreId: businessId, action: integrationType }

  useEffect(() => {
    (async () => {
      await logBiEvent(connectStoreModalShown(biParams))
      checkAuthorizationState({ correlationId })
    })()
    return () => {
      reset()
    }
  }, [])

  const handleProceedClick = async () => {
    if (step === STEP_START) {
      await logBiEvent(connectStoreModalNextClicked(biParams))
      confirmIntegrationType()
    } else if (step === STEP_INPUT_SHOPIFY_STORE_NAME) {
      shopifyNameFormRef?.current && shopifyNameFormRef.current.submit()
    } else {
      modalRef?.current && modalRef.current.dismiss()
    }
  }

  return (
    <DataModal
      ref={modalRef}
      fullWidth maxWidth="sm"
      className={style.ConnectStoreModal}
      title={t('connectStoreModal.title')}
      renderBody={() => {
        switch (step) {
          case STEP_INPUT_SHOPIFY_STORE_NAME:
            return (
              <InputShopifyNameContent
                ref={shopifyNameFormRef}
                connectStoreStore={connectStoreStore}
              />
            )
          case STEP_AUTHORIZATION_CHECK_FAILED: return (
            <AuthorizationCheckFailedContent
              integrationType={integrationType}
              checkResults={storeIntegration.authorizationCheckResults}
            />
          )
          case STEP_AUTHORIZATION_CHECK_IN_PROGRESS:
          case STEP_BACK_FROM_PERMISSIONS_GRANT:
          case STEP_AUTHORIZATION_CREATED:
            return (
              <AuthorizationInProgressContent
                success={step === STEP_AUTHORIZATION_CREATED}
              />
            )
          case STEP_CONFIRMATION_FAILED: return (
            <ConfirmationFailedContent storeIntegrationStore={storeIntegration} />
          )
          default:
            return <SelectPlatformContent connectStoreStore={connectStoreStore} />
        }
      }}
      renderActions={({ dismissModal }) => (
        <>
          <Fade in={showCancelAction}>
            <Button
              variant="outlined" color="primary"
              disabled={cancelActionDisabled}
              onClick={dismissModal}
            >
              {t('connectStoreModal.cancelAction.cta')}
            </Button>
          </Fade>
          <Fade in={showPrimaryAction}>
            <LoadingButton
              variant="contained" color="primary"
              loading={isAuthorizationInProgress}
              disabled={primaryActionDisabled}
              onClick={handleProceedClick}
            >
              {t(primaryActionLabel)}
            </LoadingButton>
          </Fade>
        </>
      )}
      onExited={onExited}
    />

  )
}

ConnectStoreModal.propTypes = {
  connectStoreStore: PropTypes.shape({
    step: PropTypes.oneOf([
      STEP_START,
      STEP_INPUT_SHOPIFY_STORE_NAME,
      STEP_AUTHORIZATION_CHECK_IN_PROGRESS,
      STEP_AUTHORIZATION_CHECK_FAILED,
      STEP_BACK_FROM_PERMISSIONS_GRANT,
      STEP_AUTHORIZATION_CREATED,
      STEP_CONFIRMATION_FAILED,
    ]).isRequired,
    businessId: PropTypes.string.isRequired,
    primaryActionLabel: PropTypes.string.isRequired,
    primaryActionDisabled: PropTypes.bool.isRequired,
    isAuthorizationInProgress: PropTypes.bool,
    showPrimaryAction: PropTypes.bool.isRequired,
    showCancelAction: PropTypes.bool.isRequired,
    cancelActionDisabled: PropTypes.bool.isRequired,
    checkAuthorizationState: PropTypes.func.isRequired,
    confirmIntegrationType: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    integrationType: PropTypes.string,
    storeIntegration: PropTypes.shape({
      authorizationCheckResults: PropTypes.object,
    }),
  }).isRequired,
  onExited: PropTypes.func,
}

export default observer(ConnectStoreModal)
